import React from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ReportPagination } from '@/components/shared/reports/ReportPagination';
import { FileText, Loader2 } from 'lucide-react';
import type { PostingLogRow, SortKey, SortDir } from '@/utils/PostingLog/types';
import { PAGE_SIZE } from '@/utils/PostingLog/constants';
import { LineTable } from './LineTable';

interface ResultsTableProps {
  data: PostingLogRow[];
  totalCount: number;
  isLoading: boolean;
  onTimesheetClick: (timesheetId: string) => void;
  onRetry: (rowId: string) => void;
  retryingIds: Set<string>;
  disabledRetryIds: Map<string, string>;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  page: number;
  onPageChange: (p: number) => void;
  allowRetry: boolean;
  columnVisibility?: Record<string, boolean>;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  data, totalCount, isLoading, onTimesheetClick, onRetry, retryingIds, disabledRetryIds,
  selectedIds, onToggleSelect, onToggleSelectAll,
  sortKey, sortDir, onSort, page, onPageChange, allowRetry, columnVisibility,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageData   = data;

  const failedOnPage = pageData.filter((r) => r.status === 'Failed');
  const allFailedOnPageSelected =
    failedOnPage.length > 0 && failedOnPage.every((r) => selectedIds.has(r.id));

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        {isLoading ? (
          <Loader2 className="h-10 w-10 opacity-30 animate-spin" />
        ) : (
          <>
            <FileText className="h-10 w-10 opacity-30" />
            <p className="text-sm">No posting records match your filters.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <LineTable
          lines={pageData}
          onTimesheetClick={onTimesheetClick}
          onRetry={onRetry}
          retryingIds={retryingIds}
          disabledRetryIds={disabledRetryIds}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          allFailedOnPageSelected={allFailedOnPageSelected}
          onToggleSelectAll={onToggleSelectAll}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          allowRetry={allowRetry}
          columnVisibility={columnVisibility}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <ReportPagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
        )}

        <p className="text-xs text-muted-foreground text-right">
          Showing{' '}
          {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min((page - 1) * PAGE_SIZE + data.length, totalCount)}{' '}
          of {totalCount} line{totalCount !== 1 ? 's' : ''}
        </p>
      </div>
    </TooltipProvider>
  );
};
